import { useElements, useStripe } from '@stripe/react-stripe-js';
import { type StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useState } from 'react';

export const useStripePaymentElements = () => {
  const stripe = useStripe();
  const elements = useElements();

  const [isFormComplete, setIsFormComplete] = useState(false);

  const handlePaymentElementOnChange = (
    event: Pick<StripePaymentElementChangeEvent, 'complete'>,
  ) => {
    if (!event.complete) {
      setIsFormComplete(false);
    } else {
      setIsFormComplete(true);
    }
  };

  const processPaymentIntent = async (paymentIntentClientSecret: string) => {
    if (!stripe || !elements) {
      return undefined;
    }

    const elementResponse = await elements.submit();

    if (elementResponse.error) {
      throw new Error(elementResponse.error.message);
    }

    const stripeResponse = await stripe.confirmPayment({
      elements,
      clientSecret: paymentIntentClientSecret,
      confirmParams: {
        return_url: process.env.WEB_CUSTOMER_HOST,
      },
      redirect: 'if_required',
    });

    if (stripeResponse.error) {
      throw new Error(stripeResponse.error.message);
    }

    return stripeResponse;
  };

  const createConfirmationToken = async () => {
    if (!stripe || !elements) {
      return undefined;
    }

    const submitResult = await elements.submit();
    if (submitResult.error) {
      throw new Error(submitResult.error.message);
    }

    const stripeResponse = await stripe.createConfirmationToken({ elements });
    if (stripeResponse.error) {
      throw new Error(stripeResponse.error.message);
    }

    return stripeResponse;
  };

  const processSetupIntent = async (setupIntentClientSecret: string) => {
    if (!stripe || !elements) {
      return undefined;
    }

    const elementResponse = await elements.submit();

    if (elementResponse.error) {
      throw new Error(elementResponse.error.message);
    }

    const stripeResponse = await stripe.confirmSetup({
      elements,
      clientSecret: setupIntentClientSecret,
      confirmParams: {
        return_url: process.env.WEB_CUSTOMER_HOST,
      },
      redirect: 'if_required',
    });

    if (stripeResponse.error) {
      throw new Error(stripeResponse.error.message);
    }

    return stripeResponse;
  };

  return {
    createConfirmationToken,
    handlePaymentElementOnChange,
    isFormComplete,
    processPaymentIntent,
    processSetupIntent,
  };
};
